import { useCallback, useEffect, useMemo, useReducer } from 'react'
import qs from 'qs'
import moment from 'moment'
import { SEARCH_VERTICALS } from 'constants/search'
import { FlightsFareTypes } from 'constants/flight'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { resetFlights } from 'actions/CartActions'
import { pushWithRegion } from 'actions/NavigationActions'
import { getFlightsRecentSearch, setFlightSessionRecentSearch, validateFlightOccupants } from 'lib/flights/flightUtils'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { isFlightsCredit, isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import FlightsTabContent from '../Components/Mobile/TabContents/FlightsTabContent'
import flightSearchWidgetStateReducer, { FlightSearchWidgetActions, initialState } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import * as Analytics from 'analytics/analytics'
import { OptimizelyExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import { getFlightSearchWidgetRecentStateData } from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidget'
import { flightsFlightSearchClick } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import { clientEvent, searchEventWithContext } from 'analytics/snowplow/events'
import {
  mapFlightSearchDataToSnowplowSearchEvent,
} from 'analytics/mapSnowplowSearchTracking'

function useGlobalSearchVerticalTabFlights(searchVerticalItem?: App.SearchVerticalItem): App.GlobalSearchVerticalTabV2 {
  const dispatch = useAppDispatch()

  const isEnabled = useAppSelector((state) => isStandaloneFlightsEnabled(state) || isFlightsCredit(state))
  const pathname = useAppSelector(state => state.router.location.pathname)
  const withRecentSearch = !!useOptimizelyExperiment(OptimizelyExperiments.flightPreFillRecentSearch) && !pathname.includes('flights-search-results')

  const [state, stateDispatch] = useReducer(flightSearchWidgetStateReducer, {
    ...initialState,
    ...(withRecentSearch ? getFlightSearchWidgetRecentStateData(getFlightsRecentSearch()) : {}),
    eventAnalytics: {
      contextLocation: 'common-search',
    },
  })

  const {
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    occupants,
    fareType,
    fareCabin,
    showDepartureError,
    showDestinationError,
    showTravelDatesError,
    showSameAirportError,
    eventAnalytics,
  } = state

  const invalidOccupantsSelection = useMemo(() => validateFlightOccupants(occupants), [occupants])

  const invalidDateSelection = useMemo(() => {
    if (fareType.value === 'return') {
      return !checkinDate || !checkoutDate
    } else {
      return !checkinDate
    }
  }, [fareType, checkinDate, checkoutDate])

  const onSearch = useCallback(() => {
    if (!departureAirport?.airportCode) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_DEPARTURE_ERROR, error: true })
    }
    if (!arrivalAirport?.airportCode) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_ARRIVAL_ERROR, error: true })
    }
    if (invalidDateSelection) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_TRAVEL_DATES_ERROR, error: true })
    }
    if (departureAirport?.airportCode === arrivalAirport?.airportCode) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_SAME_AIRPORT_ERROR, error: true })
    }
    if (!departureAirport?.airportCode || !arrivalAirport?.airportCode ||
      invalidDateSelection || occupants.childrenAge?.some(age => age === -1) ||
      departureAirport?.airportCode === arrivalAirport?.airportCode || invalidOccupantsSelection)
    {
      return false
    }

    const transformedOccupants = countOccupantsForFlights([occupants])
    const isReturnFare = fareType.value === FlightsFareTypes.RETURN
    const departDate = moment(checkinDate).format(ISO_DATE_FORMAT)
    const returnDate = isReturnFare ? moment(checkoutDate).format(ISO_DATE_FORMAT) : null

    const queryParams = {
      originAirportCode: departureAirport.airportCode,
      originAirportName: departureAirport.airportName,
      destinationAirportCode: arrivalAirport.airportCode,
      destinationAirportName: arrivalAirport.airportName,
      departDate,
      returnDate,
      adults: transformedOccupants.adults,
      children: transformedOccupants.children,
      infants: transformedOccupants.infants,
      childrenAge: transformedOccupants.childrenAge.join(','),
      fareClass: fareCabin.value,
      fareType: fareType.value,
      searchId: moment().unix(),
    }
    fireInteractionEvent(flightsFlightSearchClick())
    Analytics.track(
      'client-event',
      clientEvent({
        subject: 'flight-search-with-recent-data',
        action: 'clicked',
        category: 'logging',
        type: 'operational',
        optimizelyEventId: '29397610123',
        optimizelyEventKey: 'click-flight-search-with-recent-data',
      }),
    )

    if (withRecentSearch) {
      setFlightSessionRecentSearch({
        departingDate: departDate,
        returningDate: returnDate,
        departureAirport,
        arrivalAirport,
        fareType: fareType?.value,
        fareClass: fareCabin?.value,
        occupants,
      })
    }

    if (eventAnalytics?.contextLocation) {
      Analytics.track('search-event', searchEventWithContext(
        eventAnalytics.contextLocation,
        eventAnalytics.searchItemCategory,
        mapFlightSearchDataToSnowplowSearchEvent({
          searchVerticals: new Set([SEARCH_VERTICALS.FLIGHTS]),
          originAirportCode: departureAirport?.airportCode,
          originAirportName: departureAirport?.airportName,
          destinationAirportCode: arrivalAirport?.airportCode,
          destinationAirportName: arrivalAirport?.airportName,
          departDate,
          returnDate,
          occupancies: transformedOccupants,
          fareClass: fareCabin?.value,
          fareType: fareType?.value,
        },
        ),
      ))
    }

    dispatch(resetFlights())
    dispatch(pushWithRegion('/flights-search-results', qs.stringify(queryParams)))
  }, [departureAirport, arrivalAirport, invalidDateSelection, occupants, invalidOccupantsSelection, fareType.value, checkinDate, checkoutDate, fareCabin.value, withRecentSearch, eventAnalytics.contextLocation, eventAnalytics.searchItemCategory, dispatch])

  useEffect(() => {
    if (departureAirport?.airportCode && showDepartureError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_DEPARTURE_ERROR, error: false })
    }
    if (arrivalAirport?.airportCode && showDestinationError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_ARRIVAL_ERROR, error: false })
    }
    if (!invalidDateSelection && showTravelDatesError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_TRAVEL_DATES_ERROR, error: false })
    }
    if (departureAirport?.airportCode !== arrivalAirport?.airportCode && showSameAirportError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_SAME_AIRPORT_ERROR, error: false })
    }
  }, [
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    invalidDateSelection,
    showDepartureError,
    showDestinationError,
    showTravelDatesError,
    showSameAirportError,
  ])

  return useMemo((): App.GlobalSearchVerticalTabV2 => ({
    isEnabled: isEnabled && !!searchVerticalItem,
    label: searchVerticalItem?.label ?? 'flights',
    key: SEARCH_VERTICALS.FLIGHTS,
    context: { dispatch: stateDispatch, state },
    contextProvider: FlightSearchWidgetStateProvider,
    onSearch,
    ContentComponent: FlightsTabContent,
    Icon: LinePlaneDepartureIcon,
  }), [isEnabled, searchVerticalItem, state, onSearch])
}

export default useGlobalSearchVerticalTabFlights
